import React, { Fragment, useCallback, useEffect, useRef } from 'react';
import { useAmCharts } from '#app/hooks/useAmCharts';
import { Money } from 'finhelper';
import useChartData from '#app/pages/IncomeAndExpenses/components/BaseOperationList/Chart/hooks/useChartData';
import { ChartBox, Layout } from './components/styles';
import LegendColumn from './components/LegendColumn';

type Props = {
  preparedActives: any[];
  client?: any;
  currency?: any;
};

const Chart: React.FC<Props> & { defaultProps?: Partial<Props> } = ({ preparedActives }) => {
  const containerId = 'tacticspiechart';

  const chartRef = useRef<any | null>(null);
  const innerSeriesRef = useRef<any | null>(null);
  const [_rev, setRev] = React.useState(0);

  const { loading: chartsLoading, error: chartsError, am4core, am4charts, isReady } = useAmCharts();

  const data = useChartData(preparedActives);

  // init chart once
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (chartRef.current) return;
    if (!isReady || !am4core || !am4charts) return;

    const chart = am4core.create(containerId, am4charts.PieChart);
    chartRef.current = chart;

    chart.seriesContainer.align = 'center';
    chart.padding(0, 0, 0, 0);
    chart.radius = am4core.percent(95);
    chart.innerRadius = am4core.percent(75);
    chart.logo.disabled = true;

    const inner = chart.series.push(new am4charts.PieSeries());
    inner.dataFields.value = 'value';
    inner.dataFields.category = 'name';
    inner.slices.template.propertyFields.fill = 'color';
    inner.slices.template.stroke = am4core.color('#fff');
    inner.slices.template.strokeWidth = 2;
    inner.slices.template.strokeOpacity = 1;
    inner.slices.template.adapter.add('tooltipText', function (text: any, target: any) {
      const dataItem = target.dataItem;
      if (!dataItem) return text;

      const value = dataItem.values.value.value;
      const percent = dataItem.values.value.percent;
      const formattedValue = Money.format(value);
      const formattedPercent = Math.round(percent * 10) / 10;

      return `${dataItem.category}: ${formattedValue} ₽ (${formattedPercent}%)`;
    });
    // @ts-ignore
    inner.slices.template.states.getKey('hover').properties.scale = 1;
    // @ts-ignore
    inner.slices.template.states.getKey('hover').properties.shiftRadius = 0;
    // @ts-ignore
    inner.slices.template.states.getKey('active').properties.shiftRadius = 0;
    inner.labels.template.disabled = true;
    inner.ticks.template.disabled = true;

    const onDataValidated = (ev: any) => {
      const s: any = ev.target;
      s?.slices?.each((slice: any) => {
        const di = slice?.dataItem;
        if (!di) return;
        // @ts-ignore
        if (
          (di.values?.value?.value === 0 || di.dataContext?.type === 'group') &&
          di.legendDataItem
        ) {
          // @ts-ignore
          di.legendDataItem.dispose();
        }
      });
      setRev((x) => x + 1);
    };

    inner.events.on('datavalidated', onDataValidated);

    innerSeriesRef.current = inner;

    return () => {
      try {
        chart.dispose();
      } finally {
        chartRef.current = null;
        innerSeriesRef.current = null;
      }
    };
  }, [containerId, isReady]);

  // set data
  useEffect(() => {
    if (!chartRef.current || !isReady) return;
    chartRef.current.data = data;
  }, [data, isReady]);

  // controls from legend
  const toggleSlice = useCallback((i: number) => {
    const s = innerSeriesRef.current;
    if (!s) return;
    const di = s.dataItems.getIndex(i);
    if (!di) return;
    di.visible ? di.hide() : di.show();
  }, []);

  const hoverSlice = useCallback((i: number) => {
    const s = innerSeriesRef.current;
    if (!s) return;
    const sl = s.slices.getIndex(i);
    if (sl) sl.isHover = true;
  }, []);

  const blurSlice = useCallback((i: number) => {
    const s = innerSeriesRef.current;
    if (!s) return;
    const sl = s.slices.getIndex(i);
    if (sl) sl.isHover = false;
  }, []);

  // данные для колонки легенды - мемоизируем с зависимостью от _rev
  const leftRows = React.useMemo(() => {
    const s = innerSeriesRef.current;
    if (!s?.dataItems?.each) return [];

    const rows: Array<{ color: string; name: string; value: number; percent: number; i: number }> =
      [];
    s.dataItems.each((row: any, i: number) => {
      const isLeft = i % 2 === 1;
      if (!isLeft) return;

      rows.push({
        color: row?.dataContext?.color,
        name: row?.dataContext?.name,
        //@ts-ignore
        value: row?.dataContext?.value,
        percent: row?.values?.value?.percent ?? 0,
        i,
      });
    });

    rows.sort((a, b) => b.value - a.value);
    return rows.map((r: any) => ({
      ...r,
      valueLabel: `${Money.format(r.value)} ₽`,
      percentLabel: `${Math.round((r.percent || 0) * 10) / 10}%`,
    }));
  }, [_rev]);

  const rightRows = React.useMemo(() => {
    const s = innerSeriesRef.current;
    if (!s?.dataItems?.each) return [];

    const rows: Array<{ color: string; name: string; value: number; percent: number; i: number }> =
      [];
    s.dataItems.each((row: any, i: number) => {
      const isLeft = i % 2 === 1;
      if (isLeft) return;

      rows.push({
        color: row?.dataContext?.color,
        name: row?.dataContext?.name,
        //@ts-ignore
        value: row?.dataContext?.value,
        percent: row?.values?.value?.percent ?? 0,
        i,
      });
    });

    rows.sort((a, b) => b.value - a.value);
    return rows.map((r: any) => ({
      ...r,
      valueLabel: `${Money.format(r.value)} ₽`,
      percentLabel: `${Math.round((r.percent || 0) * 10) / 10}%`,
    }));
  }, [_rev]);

  if (chartsLoading) {
    return null;
  }

  if (chartsError) {
    return null;
  }

  return (
    <Fragment>
      <Layout>
        <LegendColumn
          rows={leftRows}
          onClick={toggleSlice}
          onHover={hoverSlice}
          onLeave={blurSlice}
          rev={_rev}
          side="left"
        />
        <ChartBox id={containerId} />
        <LegendColumn
          rows={rightRows}
          onClick={toggleSlice}
          onHover={hoverSlice}
          onLeave={blurSlice}
          rev={_rev}
          side="right"
        />
      </Layout>
    </Fragment>
  );
};

export default Chart;
